import { SignInButton, SignedIn, SignedOut, UserButton, useAuth } from "@clerk/clerk-react";
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { Button } from "@the-drop/ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Logo } from "../components/Logo";
import { resolveClerkPublishableKey } from "../clerk-env";
import { fetchAdminTransporters, type AdminTransporterOption } from "../lib/fetch-admin-transporters";
import { fetchMe, type MeUser } from "../lib/fetch-me";
import { fetchTransporterOnboarding, type OnboardingRecordDto } from "../lib/fetch-transporter-onboarding";
import {
  fetchTransporterProfile,
  type TransporterProfileDto,
} from "../lib/fetch-transporter-delivery-ops";
import {
  isTransporterOpsUser,
  resolveTransporterIdForWorkspaceUser,
  shouldShowAdminTransporterScopePicker,
} from "../lib/transporter-access";
import { useTransporterSocket } from "../lib/use-transporter-socket";
import { TransporterSocketProvider } from "./transporter-socket-context";
import { TransporterWorkspaceProvider } from "./transporter-workspace-context";

const ADMIN_SCOPE_STORAGE_KEY = "thedrop:transporter-web:adminTransporterScope";

function workspacePageTitle(pathname: string): string {
  if (pathname.startsWith("/workspace/dashboard")) return "Transporter Dashboard";
  if (pathname.startsWith("/workspace/onboarding")) return "Onboarding";
  if (pathname.startsWith("/workspace/fleet")) return "Fleet";
  if (pathname.startsWith("/workspace/run/")) return "Run detail";
  if (pathname.startsWith("/workspace/runs")) return "Runs";
  if (pathname.startsWith("/workspace/manual-dispatch")) return "Manual dispatch";
  if (pathname.startsWith("/workspace/earnings")) return "Earnings";
  if (pathname.startsWith("/workspace/account")) return "Account";
  return "Transporter";
}

function readAdminScope(): string | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const v = sessionStorage.getItem(ADMIN_SCOPE_STORAGE_KEY);
    return v != null && v.trim().length > 0 ? v.trim() : null;
  } catch {
    return null;
  }
}

function writeAdminScope(id: string): void {
  try {
    sessionStorage.setItem(ADMIN_SCOPE_STORAGE_KEY, id.trim());
  } catch {
    /* ignore */
  }
}

function clearAdminScopeStorage(): void {
  try {
    sessionStorage.removeItem(ADMIN_SCOPE_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function IconHome({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  );
}

function IconSparkles({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
      />
    </svg>
  );
}

function IconTruck({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0"
      />
    </svg>
  );
}

function IconList({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h10" />
    </svg>
  );
}

function IconClipboard({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
      />
    </svg>
  );
}

function IconCurrency({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function IconUser({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}

function IconSupport({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4h.01M11 8h2v4h-2V8z"
      />
    </svg>
  );
}

type SidebarIcon = typeof IconSparkles;

const WORKSPACE_SIDEBAR_ITEMS: {
  to:
    | "/workspace/dashboard"
    | "/workspace/onboarding"
    | "/workspace/fleet"
    | "/workspace/runs"
    | "/workspace/manual-dispatch"
    | "/workspace/earnings"
    | "/workspace/account"
    | "/workspace/support";
  label: string;
  search?: { tab: "org" };
  icon: SidebarIcon;
}[] = [
  { to: "/workspace/dashboard", label: "Dashboard", icon: IconHome },
  { to: "/workspace/onboarding", label: "Onboarding", icon: IconSparkles },
  { to: "/workspace/fleet", label: "Fleet", icon: IconTruck },
  { to: "/workspace/runs", label: "Runs", icon: IconList },
  { to: "/workspace/manual-dispatch", label: "Manual dispatch", icon: IconClipboard },
  { to: "/workspace/earnings", label: "Earnings", icon: IconCurrency },
  { to: "/workspace/account", label: "Account", search: { tab: "org" }, icon: IconUser },
  { to: "/workspace/support", label: "Support", icon: IconSupport },
];

export function WorkspaceLayout() {
  const clerkPk = resolveClerkPublishableKey();
  if (!clerkPk) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] px-4 py-12">
        <div className="mx-auto max-w-lg rounded-2xl border border-[var(--card-border)] bg-[var(--card-bg)] p-6 text-sm shadow-[var(--card-shadow)]">
          <p className="text-[var(--text-primary)]">
            Set <span className="font-mono text-sm">VITE_CLERK_PUBLISHABLE_KEY</span> in{" "}
            <span className="font-mono text-sm">apps/transporter-web/.env</span> to use the workspace (Clerk hooks
            require a provider).
          </p>
        </div>
      </div>
    );
  }
  return <WorkspaceLayoutWithClerk />;
}

/** Clerk avatar + dropdown (Manage account, Sign out). */
function ClerkAccountMenu() {
  return (
    <UserButton
      afterSignOutUrl="/"
      appearance={{
        elements: {
          avatarBox: "h-8 w-8",
        },
      }}
    />
  );
}

function WorkspaceLayoutWithClerk() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const locationSearch = useRouterState({ select: (s) => s.location.search });
  const navigate = useNavigate();
  const isDashboard = pathname.startsWith("/workspace/dashboard");
  const qFromDashboardUrl = isDashboard ? new URLSearchParams(locationSearch).get("q")?.trim() ?? "" : "";
  const [searchDraft, setSearchDraft] = useState(qFromDashboardUrl);

  useEffect(() => {
    setSearchDraft(qFromDashboardUrl);
  }, [qFromDashboardUrl]);
  const [me, setMe] = useState<MeUser | null>(null);
  const [meError, setMeError] = useState<string | null>(null);
  const [adminScope, setAdminScope] = useState<string | null>(() => readAdminScope());
  const [adminTransporters, setAdminTransporters] = useState<AdminTransporterOption[]>([]);
  const [adminListStatus, setAdminListStatus] = useState<"idle" | "loading" | "ok" | "error">("idle");
  const [adminListError, setAdminListError] = useState<string | null>(null);

  const pageTitle = workspacePageTitle(pathname);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      setMe(null);
      setMeError(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const r = await fetchMe(getToken);
        if (!cancelled) {
          setMe(r.user);
          setMeError(null);
        }
      } catch {
        if (!cancelled) {
          setMe(null);
          setMeError("Could not load your session. Try refreshing or signing in again.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, getToken]);

  const showAdminTransporterScope = useMemo(
    () => (me ? shouldShowAdminTransporterScopePicker(me) : false),
    [me],
  );

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !me || !showAdminTransporterScope) {
      setAdminTransporters([]);
      setAdminListStatus("idle");
      setAdminListError(null);
      return;
    }
    let cancelled = false;
    setAdminListStatus("loading");
    setAdminListError(null);
    void (async () => {
      try {
        const list = await fetchAdminTransporters(getToken);
        if (cancelled) return;
        setAdminTransporters(list);
        const stored = readAdminScope();
        const validStored = stored != null && list.some((t) => t.id === stored);
        if (validStored) {
          setAdminScope(stored);
        } else if (list.length > 0) {
          const next = list[0]!.id;
          writeAdminScope(next);
          setAdminScope(next);
        } else {
          setAdminScope(null);
          clearAdminScopeStorage();
        }
        setAdminListStatus("ok");
      } catch (e) {
        if (!cancelled) {
          setAdminTransporters([]);
          setAdminListStatus("error");
          setAdminListError(e instanceof Error ? e.message : "admin_transporters_failed");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, me, getToken, showAdminTransporterScope]);

  const transporterId = useMemo(() => {
    if (!me) return null;
    return resolveTransporterIdForWorkspaceUser(me, adminScope);
  }, [me, adminScope]);

  const [workspaceProfile, setWorkspaceProfile] = useState<TransporterProfileDto | null>(null);
  const [onboardingRecords, setOnboardingRecords] = useState<OnboardingRecordDto[] | null>(null);
  const [workspaceProfileLoading, setWorkspaceProfileLoading] = useState(false);

  const refreshWorkspaceProfile = useCallback(async () => {
    if (!transporterId) {
      setWorkspaceProfile(null);
      setOnboardingRecords(null);
      setWorkspaceProfileLoading(false);
      return;
    }
    setWorkspaceProfileLoading(true);
    try {
      const [p, o] = await Promise.all([
        fetchTransporterProfile(getToken, transporterId),
        fetchTransporterOnboarding(getToken, transporterId),
      ]);
      setWorkspaceProfile(p);
      setOnboardingRecords(o.records);
    } catch {
      setWorkspaceProfile(null);
      setOnboardingRecords(null);
    } finally {
      setWorkspaceProfileLoading(false);
    }
  }, [transporterId, getToken]);

  useEffect(() => {
    void refreshWorkspaceProfile();
  }, [refreshWorkspaceProfile]);

  const visibleSidebarItems = useMemo(
    () =>
      WORKSPACE_SIDEBAR_ITEMS.filter(
        (item) => item.to !== "/workspace/onboarding" || !workspaceProfile?.onboardingCompletedAt,
      ),
    [workspaceProfile?.onboardingCompletedAt],
  );

  const onAdminTransporterSelect = useCallback((id: string) => {
    if (id.length === 0) return;
    writeAdminScope(id);
    setAdminScope(id);
  }, []);

  const [fleetRefreshNonce, setFleetRefreshNonce] = useState(0);
  const fleetDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (fleetDebounceRef.current != null) {
        clearTimeout(fleetDebounceRef.current);
      }
    };
  }, []);

  const scheduleFleetDataRefresh = useCallback(() => {
    if (fleetDebounceRef.current != null) {
      clearTimeout(fleetDebounceRef.current);
    }
    fleetDebounceRef.current = setTimeout(() => {
      fleetDebounceRef.current = null;
      setFleetRefreshNonce((n) => n + 1);
    }, 1000);
  }, []);

  const { socket: transporterSocket } = useTransporterSocket(
    transporterId,
    getToken,
    Boolean(isSignedIn && me && isTransporterOpsUser(me) && transporterId),
    scheduleFleetDataRefresh,
  );

  const opsShell = Boolean(isSignedIn && me && isTransporterOpsUser(me));

  /** Clerk: `SignedIn` / `SignedOut` render nothing until `isLoaded` — without this the shell is blank. */
  if (!isLoaded) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[var(--page-bg)] px-6 py-16">
        <p className="text-sm text-[var(--text-secondary)]">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--page-bg)]">
      <p className="hidden max-[959px]:block border-b border-amber-light bg-amber-bg px-4 py-2 text-center text-2xs font-medium text-[#854D0E]">
        Best viewed on a desktop display (1200px or wider recommended).
      </p>

      <SignedOut>
        <div className="flex min-h-screen flex-col">
          <header className="flex h-16 shrink-0 items-center border-b border-[var(--card-border)] bg-[var(--card-bg)] px-6 lg:px-7">
            <Link to="/workspace/dashboard" className="inline-block" aria-label="Transporter home">
              <Logo size="md" tone="dark" />
            </Link>
          </header>
          <main className="flex flex-1 flex-col justify-center px-6 py-12">
            <div className="mx-auto w-full max-w-md">
              <div className="rounded-2xl border border-[var(--card-border)] bg-[var(--card-bg)] p-8 shadow-[var(--card-shadow)]">
                <h1 className="text-xl font-bold text-[var(--text-primary)]">Transporter operations</h1>
                <p className="mt-2 text-sm leading-relaxed text-[var(--text-secondary)]">
                  Sign in with a transporter operations or platform admin account.
                </p>
                <div className="mt-8">
                  <SignInButton mode="modal">
                    <Button type="button" variant="green" size="md">
                      Sign in
                    </Button>
                  </SignInButton>
                </div>
              </div>
            </div>
          </main>
        </div>
      </SignedOut>

      <SignedIn>
        {!opsShell ? (
          <>
            <header className="flex h-16 items-center justify-between border-b border-[var(--card-border)] bg-[var(--card-bg)] px-6 lg:px-7">
              <Link to="/" className="inline-block" aria-label="Home">
                <Logo size="md" tone="dark" />
              </Link>
              <ClerkAccountMenu />
            </header>
            <main className="mx-auto max-w-7xl px-6 py-8">
              {!isLoaded || (isSignedIn && me === null && meError === null) ? (
                <p className="text-sm text-gray">Loading profile…</p>
              ) : null}
              {meError ? <p className="text-sm text-red">{meError}</p> : null}
              {me && !isTransporterOpsUser(me) ? (
                <div className="rounded-md border border-amber-light bg-amber-bg px-4 py-3 text-sm text-text">
                  <p className="font-medium text-dark">This portal is for transporter operations staff.</p>
                  <p className="mt-2 text-gray">
                    Your roles:{" "}
                    <span className="text-text">
                      {me.roles != null && me.roles.length > 0 ? me.roles.join(", ") : me.role}
                    </span>
                    . Use the customer, dispensary, or driver app instead.
                  </p>
                </div>
              ) : null}
            </main>
          </>
        ) : (
          <div className="flex min-h-screen">
            <aside className="fixed left-0 top-0 z-30 flex h-screen w-[220px] flex-col border-r border-white/10 bg-[#111111] px-3 py-6">
              <div className="mb-6 border-b border-white/10 px-2 pb-5">
                <Link to="/workspace/dashboard" className="inline-block">
                  <Logo size="md" tone="light" />
                </Link>
              </div>
              <nav className="flex flex-1 flex-col gap-1 overflow-y-auto">
                {visibleSidebarItems.map((item) => {
                  const Icon = item.icon;
                  const active = pathname === item.to || pathname.startsWith(`${item.to}/`);
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      {...(item.search ? { search: item.search } : {})}
                      className={`mx-1 flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors duration-150 ${
                        active
                          ? "bg-white font-semibold text-[#111111]"
                          : "text-[#888888] hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  );
                })}
              </nav>
              <div className="mt-auto flex items-center gap-2 border-t border-white/10 py-4">
                <Logo size="sm" tone="light" />
              </div>
            </aside>

            <div className="ml-[220px] flex min-h-screen flex-1 flex-col">
              <header className="fixed left-[220px] right-0 top-0 z-20 flex h-16 min-h-[4rem] items-center gap-2 border-b border-[#EBEBEB] bg-white px-4 sm:px-7">
                <h1 className="shrink-0 text-lg font-bold text-[#111111] sm:text-xl">{pageTitle}</h1>
                <div className="min-w-0 flex-1 px-1 sm:px-2">
                  <label className="relative block">
                    <span className="sr-only">Search dashboard</span>
                    <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-[#555555]" />
                    <input
                      type="search"
                      name="q"
                      value={searchDraft}
                      title={isDashboard ? undefined : "Press Enter to search the dashboard"}
                      onChange={(e) => {
                        const v = e.target.value;
                        setSearchDraft(v);
                        if (!isDashboard) return;
                        void navigate({
                          to: "/workspace/dashboard",
                          search: v.trim() ? { q: v.trim() } : {},
                          replace: true,
                        });
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== "Enter") return;
                        e.preventDefault();
                        const v = e.currentTarget.value.trim();
                        void navigate({
                          to: "/workspace/dashboard",
                          search: v ? { q: v } : {},
                        });
                      }}
                      placeholder="Search drivers, runs, dispensaries…"
                      autoComplete="off"
                      className="h-10 w-full max-w-2xl rounded-full border border-[#EBEBEB] bg-[#F5F5F3] py-2 pl-10 pr-3 text-sm text-[#111111] placeholder:text-[#888888] focus:border-[#111111] focus:outline-none focus:ring-1 focus:ring-[#111111]"
                    />
                  </label>
                </div>
                <div className="flex shrink-0 items-center gap-2 sm:gap-4">
                  <div className="relative">
                    <button type="button" className="relative flex h-9 w-9 items-center justify-center rounded-full hover:bg-[#F5F5F3]" aria-label="Notifications">
                      <BellIcon className="h-5 w-5 text-[#333333]" />
                      <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-[#E53E3E] text-[9px] font-bold text-white">
                        7
                      </span>
                    </button>
                  </div>
                  <ClerkAccountMenu />
                </div>
              </header>

              <main className="flex-1 overflow-y-auto bg-td-page pt-16">
                <div className="w-full px-7 py-7">
                {!isLoaded || (isSignedIn && me === null && meError === null) ? (
                  <p className="text-sm text-gray">Loading profile…</p>
                ) : null}
                {meError ? <p className="text-sm text-red">{meError}</p> : null}

                {me && showAdminTransporterScope ? (
                  <section className="td-surface mb-6 p-6">
                    <h2 className="text-sm font-medium text-td-ink">Platform admin · transporter scope</h2>
                    <p className="mt-1 text-xs text-td-muted">
                      Choose which transporter you are working with. Your choice is remembered for this browser tab.
                    </p>
                    <AdminTransporterScopePicker
                      transporters={adminTransporters}
                      selectedId={adminScope}
                      listStatus={adminListStatus}
                      listError={adminListError}
                      onSelect={onAdminTransporterSelect}
                    />
                  </section>
                ) : null}

                {transporterId ? null : me && showAdminTransporterScope && (adminListStatus === "idle" || adminListStatus === "loading") ? (
                  <p className="mb-6 text-sm text-gray">Loading transporter list…</p>
                ) : me && showAdminTransporterScope && adminListStatus === "error" ? (
                  <p className="mb-6 text-sm text-red">Could not load the transporter list. Try again later.</p>
                ) : me && showAdminTransporterScope && adminListStatus === "ok" && !transporterId ? (
                  <p className="mb-6 text-sm text-amber">No transporters exist yet. Create one in the admin console.</p>
                ) : (
                  <p className="mb-6 text-sm text-amber">
                    Your account is not linked to a transporter yet. Ask an administrator to assign access.
                  </p>
                )}

                <TransporterWorkspaceProvider
                  value={{
                    transporterId,
                    getToken,
                    fleetRefreshNonce,
                    workspaceProfile,
                    onboardingRecords,
                    workspaceProfileLoading,
                    refreshWorkspaceProfile,
                  }}
                >
                  <TransporterSocketProvider socket={transporterSocket}>
                    <Outlet />
                  </TransporterSocketProvider>
                </TransporterWorkspaceProvider>
                </div>
              </main>
            </div>
          </div>
        )}
      </SignedIn>
    </div>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );
}

function BellIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  );
}

function transporterOptionLabel(t: AdminTransporterOption): string {
  const d = t.dbaName?.trim();
  return d && d.length > 0 ? d : t.legalName;
}

function AdminTransporterScopePicker({
  transporters,
  selectedId,
  listStatus,
  listError: _listError,
  onSelect,
}: {
  transporters: AdminTransporterOption[];
  selectedId: string | null;
  listStatus: "idle" | "loading" | "ok" | "error";
  listError: string | null;
  onSelect: (id: string) => void;
}) {
  if (listStatus === "idle" || listStatus === "loading") {
    return <p className="mt-3 text-xs text-gray">Loading transporters…</p>;
  }
  if (listStatus === "error") {
    return (
      <p className="mt-3 text-xs text-red">
        Could not load transporters. Try again later.
      </p>
    );
  }
  if (transporters.length === 0) {
    return <p className="mt-3 text-xs text-gray">No transporters are available yet.</p>;
  }
  return (
    <label className="mt-3 block max-w-xl text-xs text-gray">
      Active transporter
      <select
        className="input-field mt-1"
        value={selectedId ?? ""}
        onChange={(e) => {
          const id = e.target.value;
          if (id.length > 0) onSelect(id);
        }}
      >
        {transporters.map((t) => (
          <option key={t.id} value={t.id}>
            {transporterOptionLabel(t)} ({t.id.slice(0, 8)}…)
          </option>
        ))}
      </select>
    </label>
  );
}
